// Dependencies
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

// Types
import { OperatorsType, TableProps, getOperatorsColumnNames } from './types';

// Mocks
import { mockData } from './mocks';

// Styles
import {
  Container,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../SummaryTablesStyles';

// Styled Components
import { TableContainerStyled } from './styles';

export const OperatorsTable = ({ data, ...props }: TableProps): JSX.Element => {
  const { t } = useTranslation(['tables', 'common']);
  const dataAll = data ? data : mockData;

  const columnHelper = createColumnHelper<OperatorsType>();
  const translatedColumnNames = useMemo(() => getOperatorsColumnNames(t), [t]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.nom, {
        id: translatedColumnNames.nom.id,
        header: () => translatedColumnNames.nom.label,
      }),
      columnHelper.accessor((row) => row.cognom, {
        id: translatedColumnNames.cognom.id,
        header: () => translatedColumnNames.cognom.label,
      }),
      columnHelper.accessor((row) => row.concession, {
        id: translatedColumnNames.concession.id,
        header: () => translatedColumnNames.concession.label,
      }),
    ],
    [translatedColumnNames]
  );

  const table = useReactTable({
    data: dataAll,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Container>
      <TableContainerStyled>
        <Table {...props}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainerStyled>
    </Container>
  );
};
